import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import SlideBase from './SlideBase'
import WrappedYearAnimation from '../WrappedYearAnimation'
import FadeBackground from '../FadeBackground'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import { useAppSelector } from 'hooks/reduxHooks'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'intro')
const SLIDE_ID = 'intro' as const

function IntroSlide() {
  const intl = useIntl()
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)

  // Use the video background with fallback hook
  const { background, isBackgroundReady, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const [animate, setAnimate] = useState(!SLIDE_CONFIG?.video) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  const firstName = useAppSelector(state => state.auth.account.givenName)
  const intro = firstName ? `${firstName}, ` : ''

  // Start animation when background is ready (video loaded or fallback image)
  useEffect(() => {
    if (SLIDE_CONFIG?.video && isBackgroundReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isBackgroundReady, isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const hasVisited = !!data?.stats.citiesVisited.length

  // Extract ternary heading variants as constants
  const headingTotalsOnly = intl.formatMessage({
    id: 'anFQ1/VOy/',
    defaultMessage: 'let\u2019s look back at a year of travel with Luxury Escapes!',
    description: 'Intro slide heading for totals-only mode',
  })
  const headingHasVisited = intl.formatMessage({
    id: 'E0Nr4yq+5E',
    defaultMessage: 'let\u2019s look back at your escapes!',
    description: 'Intro slide heading when user has visited cities',
  })
  const headingNoVisits = intl.formatMessage({
    id: 'MM6ejcJMxz',
    defaultMessage: 'let\u2019s look back at a year of travel with Luxury Escapes!',
    description: 'Intro slide heading when user has no visited cities',
  })

  const heading = isTotalsOnlyMode
    ? headingTotalsOnly
    : hasVisited ? headingHasVisited : headingNoVisits

  // Extract ternary caption variants as constants
  const captionA = intl.formatMessage({
    id: 'R/WlkYqjXb',
    defaultMessage: 'A Year With Luxury Escapes',
    description: 'Intro slide caption for totals-only mode',
  })
  const captionYour = intl.formatMessage({
    id: 'mk5vgZQSNL',
    defaultMessage: 'Your Year With Luxury Escapes',
    description: 'Intro slide caption for user mode',
  })
  const caption = isTotalsOnlyMode ? captionA : captionYour
  return (
    <SlideBase background={background}>
      {animate &&
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" horizontalAlign="start" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">{caption}</Caption>
              <Heading variant="heading2" format="propercase">
                {intro}{heading}
              </Heading>
            </Group>
            <WrappedYearAnimation />
          </Group>
        </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(IntroSlide)
